
import { Component, inject, OnInit, OnDestroy, signal, computed } from '@angular/core';
import { Router, RouterModule, NavigationEnd, Params } from '@angular/router';
import { CommonModule } from '@angular/common';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AppStorageService, StorageKey } from '../../services/app-storage.service';
import { 
  selectHasIdentity, 
  selectDeviceLoading, 
  selectIsDisconnected 
} from '../../store/device/device.selectors';

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [RouterModule, CommonModule],
  templateUrl: './sidebar.html',
  styleUrl: './sidebar.css',
  host: {
    '[class.collapsed]': 'isCollapsed()'
  }
})
export class Sidebar implements OnInit, OnDestroy {
  private store = inject(Store);
  private router = inject(Router);
  private storage = inject(AppStorageService);
  private sub = new Subscription();

  // Sidebar collapsed state
  isCollapsed = signal(false);

  // Tools submenu visibility
  showToolsMenu = signal(false);

  // Current URL signal for reactivity
  currentUrl = signal<string>('');

  // Active submenu for manual toggle (device vs crypto)
  // Only show submenu for device routes, not for dashboard
  activeSubmenu = signal<string | null>(null);

  // Computed signal to detect if we're on a device route
  isOnDeviceRoute = computed(() => {
    const url = this.currentUrl().split('?')[0];
    return url.startsWith('/device') || url.startsWith('/telemetry-history');
  });

  // Computed signal to detect if we're on a server route
  isOnServerRoute = computed(() => {
    const url = this.currentUrl().split('?')[0];
    return url.startsWith('/server');
  });

  // Computed signal to show Device submenu — only when activeSubmenu is 'device'
  showDeviceSubmenu = computed(() => {
    return this.activeSubmenu() === 'device';
  });

  // Computed signal to show Server submenu — only when activeSubmenu is 'server'
  isServerAvailable = computed(() => {
    return this.activeSubmenu() === 'server';
  });

  // Signals for template binding (updated from NgRx store)
  private _hasIdentity = signal(false);
  private _loading = signal(false);
  private _isDisconnected = signal(false);

  /** true if a device identity has been processed */
  hasIdentity = computed(() => this._hasIdentity());

  /** true while the app is fetching/processing the device id */
  loading = computed(() => this._loading());

  /** true if device is disconnected */
  isDisconnected = computed(() => this._isDisconnected());

  /** Query params needed to preserve the current device context across tabs */
  deviceRouteQueryParams = computed<Params>(() => {
    this.currentUrl();

    const urlTree = this.router.parseUrl(this.router.url || '/');
    const currentQueryParams = urlTree.queryParams ?? {};

    const deviceId = currentQueryParams['deviceId'] || this.storage.getString(StorageKey.CURRENT_DEVICE_ID);
    const environment = currentQueryParams['environment'] || this.storage.getString(StorageKey.CURRENT_ENVIRONMENT);
    const currentStack = currentQueryParams['currentStack'] || this.storage.getString(StorageKey.CURRENT_STACK);
    const hubName = currentQueryParams['hubName'] || this.storage.getString(StorageKey.CURRENT_HUB_NAME);

    const queryParams: Params = {};

    if (deviceId) queryParams['deviceId'] = deviceId;
    if (environment) queryParams['environment'] = environment;
    if (currentStack) queryParams['currentStack'] = currentStack;
    if (hubName) queryParams['hubName'] = hubName;

    return queryParams;
  });

  ngOnInit(): void {
    // Initialize currentUrl immediately
    this.currentUrl.set(this.router.url);

    // Initialize activeSubmenu based on current URL with a small delay to ensure router is ready
    setTimeout(() => {
      this.updateActiveSubmenu();
    }, 0);

    this.sub.add(
      this.store.select(selectHasIdentity).subscribe(v => this._hasIdentity.set(v))
    );
    this.sub.add(
      this.store.select(selectDeviceLoading).subscribe(v => this._loading.set(v))
    );
    this.sub.add(
      this.store.select(selectIsDisconnected).subscribe(v => this._isDisconnected.set(v))
    );

    // Listen to navigation end events to update submenu based on route
    this.sub.add(
      this.router.events.pipe(
        filter(event => event instanceof NavigationEnd)
      ).subscribe(() => {
        this.updateActiveSubmenu();
      })
    );
  }

  /** Update active submenu based on current URL */
  private updateActiveSubmenu(): void {
    const url = this.router.url || '/';
    this.currentUrl.set(url);
    const cleanUrl = url.split('?')[0];
    if (cleanUrl.startsWith('/device') || cleanUrl.startsWith('/telemetry-history')) {
      this.activeSubmenu.set('device');
      this.showToolsMenu.set(false);
    } else if (cleanUrl.startsWith('/server')) {
      this.activeSubmenu.set('server');
      this.showToolsMenu.set(false);
    } else if (cleanUrl.startsWith('/crypto')) {
      this.activeSubmenu.set(null);
      this.showToolsMenu.set(true);
    } else {
      this.activeSubmenu.set(null);
    }
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  /** true if route is enabled (connected or twin page for disconnected) */
  isRouteEnabled(routeName: string): boolean {
    // If disconnected, only allow Device Twin
    if (this._isDisconnected()) {
      return routeName === 'device-twin';
    }
    // If connected, check normal conditions
    return this._hasIdentity() && !this._loading();
  }

  /** prevent navigation when disabled */
  onProtectedClick(e: MouseEvent, enabled: boolean) {
    if (!enabled) {
      e.preventDefault();
      e.stopPropagation();
    }
  }

  /** Toggle sidebar collapsed state */
  toggleSidebar(): void {
    this.isCollapsed.update(v => !v);
  }

  /** Toggle submenu visibility — collapses Utility when opening device/server */
  toggleSubmenu(menuName: string): void {
    const next = this.activeSubmenu() === menuName ? null : menuName;
    this.activeSubmenu.set(next);
    if (next !== null) {
      this.showToolsMenu.set(false);
    }
  }

  /** Toggle Tools menu visibility — collapses device/server when opening Utility */
  toggleToolsMenu(): void {
    const next = !this.showToolsMenu();
    this.showToolsMenu.set(next);
    if (next) {
      this.activeSubmenu.set(null);
    }
  }
}
